import React, { useState, useEffect, useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import Modal from '@mui/material/Modal';
import CloseIcon from '@src/componentsNew/Icons/Icons/CloseIcon';

import './add-to-cart-modal-style.scss';
import { CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';
import useIsComDomain from '@src/hooks/useIsComDomain';

import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import SectionDivider from '@src/componentsNew/SectionDivider';
import MobileNotifyAlertModal from '@src/componentsNew/Modals/NotifyAlertModal/MobileNotifyAlertModal';
import OTPVerificationModal from '@src/componentsNew/Modals/OTPVerificationModal/OTPVerificationModal';

import { ME_FOR_NOTIFY_ME } from '@molecules/NotifyMeModal/queries';
import { meForNotifyMe } from '@molecules/NotifyMeModal/__generated__/meForNotifyMe';
import { product_product_variants } from '@components/ProductPage/ProductDetailPage/__generated__/product';

import { getPriceInfoFromProduct, getPriceInfoFromVariant, formatPrice } from '@src/utils';
import { StateOfVariantForAddToCart } from '@src/utils/addToCart';
import { AddCartModalProduct, AddCartModalProductVariant } from '@src/utils/types';

interface IProps {
  open: boolean;
  onClose?: any;
  type: 'desktop_thumbnail' | 'mobile_detail' | 'mobile_thumbnail';
  isMobile: boolean;
  loading?: boolean;
  product: AddCartModalProduct;
  modalTitle: string;
  onClickAddToCartBtn?: any;
  onClickNotifyMeBtn?: any;
  selectedVariantFromParent?: product_product_variants | null; // selected variant that is synchronized with the parent component's state. Current only use for mobile detail
  setSelectedVariantFromParent?: React.Dispatch<React.SetStateAction<product_product_variants>>; // Function to update the parent component's selected variant state. Current only use for mobile detail
}

const AddToCartModal = ({
  open,
  onClose,
  type,
  isMobile,
  loading,
  product,
  modalTitle,
  onClickAddToCartBtn,
  onClickNotifyMeBtn,
  selectedVariantFromParent,
  setSelectedVariantFromParent,
}: IProps) => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const isComDomain = useIsComDomain();
  const { currentUser, currentCart, setCurrentUser } = useContext<credentialType>(CredentialContext);

  const [isFetchMeInfo, setIsFetchMeInfo] = useState(false);
  const [variants] = useState<AddCartModalProductVariant[]>(product?.variants);
  const [modalSelectedVariant, setModalSelectedVariant] = useState(
    selectedVariantFromParent ? selectedVariantFromParent : product?.variants[0],
  );
  const [stateOfVariant, setStateOfVariant] = useState(StateOfVariantForAddToCart(modalSelectedVariant));

  const [isNotifyAlertModalOpen, setIsNotifyAlertModal] = useState(false); // Mobile View Alert
  const [isOTPVerificationModalOpen, setIsOTPVerificationModal] = useState(false); // Desktop View Alert

  const initialPriceInfo = getPriceInfoFromProduct(product);

  const [priceInfoObj, setPriceInfoObj] = useState({
    originalPrice: initialPriceInfo.originalPrice,
    discountPrice: initialPriceInfo.discountPrice,
    finalPrice: initialPriceInfo.finalPrice,
    discountPercent: initialPriceInfo.discountPercent,
    isOnSale: initialPriceInfo.isOnSale,
  });

  const { originalPrice, finalPrice, discountPercent, isOnSale } = priceInfoObj;

  const [meForNotifyMe] = useLazyQuery<meForNotifyMe>(ME_FOR_NOTIFY_ME, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const mobileNumber = data?.me?.mobileNumber;
      const mobileVerified = data?.me?.mobileVerified;

      const copyCurrentUser = JSON.parse(JSON.stringify(currentUser));
      copyCurrentUser.user.mobileNumber = Number(mobileNumber);
      copyCurrentUser.user.mobileVerified = mobileVerified;

      // local user info update
      setCurrentUser(copyCurrentUser);
      setIsFetchMeInfo(true);
    },
  });

  useEffect(() => {
    if (modalSelectedVariant) {
      setPriceInfoObj(getPriceInfoFromVariant(modalSelectedVariant));
    } else {
      setPriceInfoObj(getPriceInfoFromProduct(product));
    }

    const stateOfVariant = StateOfVariantForAddToCart(modalSelectedVariant);
    setStateOfVariant(stateOfVariant);

    if (currentUser?.token && !isFetchMeInfo && stateOfVariant === 'notifyMe') {
      meForNotifyMe();
    }
  }, [modalSelectedVariant]);

  const handleClickVariant = (variant) => {
    if (variant.id === modalSelectedVariant?.id) {
      setModalSelectedVariant(null);
      if (setSelectedVariantFromParent) {
        setSelectedVariantFromParent(null);
      }
    } else {
      setModalSelectedVariant(variant);
      if (setSelectedVariantFromParent) {
        setSelectedVariantFromParent(variant);
      }
    }
  };

  const handleClickBtn = (e, isBuyNow = false) => {
    if (modalSelectedVariant.stockQuantity > 0) {
      const productTypeIsMgs = product?.productType?.isMgs;
      const currentTypeCartToken = productTypeIsMgs ? currentCart?.mgsCart?.token : currentCart?.maccaronCart?.token;

      onClickAddToCartBtn(productTypeIsMgs, currentTypeCartToken, modalSelectedVariant, 1, isBuyNow, () => {
        showSnackbar({ message: 'Product Added to Bag' });
      });
    } else {
      if (!currentUser?.token) {
        showSnackbar({ message: 'You should login' });

        return;
      } else {
        if (currentUser?.user?.mobileNumber?.toString() !== '9999999999' && currentUser?.user?.mobileVerified) {
          onClickNotifyMeBtn({ variables: { variantId: modalSelectedVariant.id } });

          if (type === 'mobile_detail' || type === 'mobile_thumbnail') {
            onClose();
          }
        } else {
          if (type === 'mobile_detail' || type === 'mobile_thumbnail') {
            setIsNotifyAlertModal(true);
          } else if (type === 'desktop_thumbnail') {
            setIsOTPVerificationModal(true);
          }
        }
      }
    }
  };

  const extractNotVariantImages = (product: AddCartModalProduct) => {
    let variantImageIds = new Set();

    for (const variant of product.variants) {
      for (const image of variant.images) {
        variantImageIds.add(image.id);
      }
    }

    const notVariantBigImages = product.images.filter((image) => !variantImageIds.has(image.id));

    return notVariantBigImages;
  };

  return (
    <Modal open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <div id="add-to-cart-modal-content" className={`${isMobile ? 'mobile' : 'desktop'}`}>
        <div className="product-image-container">
          <div className="close-btn-wrapper">
            <Typography cName="product-image-title" variant="subtitle1" component="span" style={{ color: '#090A0FDE' }}>
              {modalTitle}
            </Typography>

            <CloseIcon cName="close-btn" onClick={onClose} />
          </div>

          <ul className="product-image-wrapper">
            {modalSelectedVariant
              ? modalSelectedVariant?.images.map((image) => (
                  <li key={image.id}>
                    <img src={image.url} />
                  </li>
                ))
              : extractNotVariantImages(product)?.map((image) => (
                  <li key={image.id}>
                    <img src={image.url} />
                  </li>
                ))}
          </ul>
        </div>

        <div className="product-description-wrapper">
          <div className="product-name-wrapper">
            <div className="brand-name-wrapper">
              <Typography cName="brand-name" variant="body3" component="span" style={{ color: '#090A0FDE' }}>
                {product.brand}
              </Typography>

              {type === 'mobile_thumbnail' ? <Link to={product.urlForGraphql}>See Details</Link> : null}
            </div>

            <Typography cName="product-name" variant="subtitle2" component="span" style={{ color: '#090A0FDE' }}>
              {product.name}
            </Typography>
          </div>

          <div className="product-price-wrapper">
            {isOnSale && (
              <Typography variant="h7" component="span" style={{ color: '#FF3399' }}>
                {discountPercent}%
              </Typography>
            )}
            <Typography variant="h7" component="span" style={{ color: '#212121' }}>
              {formatPrice(finalPrice)}
            </Typography>
            {isOnSale && (
              <Typography variant="strikethrough2" component="span" style={{ color: '#666666' }}>
                {formatPrice(originalPrice)}
              </Typography>
            )}
          </div>
        </div>

        <SectionDivider style={{ height: '1px' }} />

        <div className="product-variants-wrapper">
          <div className="variant-name-wrapper">
            <Typography
              variant="body3"
              component="span"
              style={{
                color: modalSelectedVariant && modalSelectedVariant.stockQuantity > 0 ? '#090A0FDE' : '#11141D99',
              }}
            >
              {modalSelectedVariant ? modalSelectedVariant?.name : 'Please Select Variants'}
            </Typography>

            {modalSelectedVariant && (
              <>
                {modalSelectedVariant.isEndOfLife && modalSelectedVariant.stockQuantity <= 0 && (
                  <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
                    {` | Discontinued`}
                  </Typography>
                )}
                {!modalSelectedVariant.isEndOfLife && modalSelectedVariant.stockQuantity <= 0 && (
                  <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
                    {` | Out of stock`}
                  </Typography>
                )}
                {modalSelectedVariant.stockQuantity <= 5 && modalSelectedVariant.stockQuantity > 0 && (
                  <Typography variant="body3" component="span" style={{ color: '#DD2626' }}>
                    {` | ${modalSelectedVariant.stockQuantity} left only!`}
                  </Typography>
                )}
              </>
            )}
          </div>

          <ul className="variant-image-wrapper">
            {variants.map((variant) => (
              <li
                key={variant.id}
                className={`${variant.id === modalSelectedVariant?.id ? 'selected' : ''}`}
                onClick={() => handleClickVariant(variant)}
              >
                <img
                  src={variant?.attributes[0]?.value?.image?.url}
                  className={variant.stockQuantity <= 0 ? 'overlay' : ''}
                />
                {variant.stockQuantity <= 0 ? <div className="img-out-of-stock-line" /> : null}
              </li>
            ))}
          </ul>
        </div>

        <div className="add-to-cart-btn-wrapper">
          {type === 'desktop_thumbnail' && (
            <Button
              size="large"
              cName={`${isComDomain ? 'com' : ''}`}
              variant="contained"
              disabled={loading}
              onClick={() => history.push(product.urlForGraphql)}
            >
              View Detail
            </Button>
          )}
          {stateOfVariant === 'discontinued' && (
            <Button size="large" variant="contained" disabled={true}>
              Discontinued
            </Button>
          )}
          {stateOfVariant === 'notifyRequested' && (
            <Button size="large" variant="contained" disabled={true}>
              Notify Me Requested
            </Button>
          )}
          {stateOfVariant === 'notifyMe' && (
            <Button
              size="large"
              cName={`${isComDomain ? 'com' : ''}`}
              variant="contained"
              disabled={loading}
              onClick={handleClickBtn}
            >
              Notify Me
            </Button>
          )}
          {modalSelectedVariant && stateOfVariant === 'addToCart' && (
            <>
              <Button
                size="large"
                cName={`${isComDomain ? 'com' : ''}`}
                variant="contained"
                disabled={loading}
                onClick={(e) => handleClickBtn(e, false)}
              >
                Add To Bag
              </Button>
              {type !== 'desktop_thumbnail' && (
                <Button
                  size="large"
                  cName={`${isComDomain ? 'com' : ''}`}
                  variant="contained"
                  disabled={loading}
                  onClick={(e) => handleClickBtn(e, true)}
                >
                  Buy Now
                </Button>
              )}
            </>
          )}
          {!modalSelectedVariant && (
            <Button size="large" variant="contained" disabled={true}>
              Select Variants
            </Button>
          )}
        </div>

        {isMobile && isNotifyAlertModalOpen && (
          <MobileNotifyAlertModal open={isNotifyAlertModalOpen} onClose={() => setIsNotifyAlertModal(false)} />
        )}

        {!isMobile && isOTPVerificationModalOpen && (
          <OTPVerificationModal open={isOTPVerificationModalOpen} onClose={() => setIsOTPVerificationModal(false)} />
        )}
      </div>
    </Modal>
  );
};

export default AddToCartModal;
